import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { HelpCircle } from 'lucide-react'
import { cn } from '@/utils/cn'
import logo from "@/assets/vvslogo.png";
import { useAuthStore } from '@/store/authStore'
import { useValuationStore } from '@/store/valuationStore'
import { useTourUiStore } from '@/store/tourStore'
import { clearFormRecognizerPhotos } from '@/utils/clearFormRecognizerPhotos'
import { useSaveDraft } from '@/hooks/useValuationMutations'
import { getMessage } from '@/utils/messages'
import { UnsavedChangesLogoutDialog } from '@/components/ui/UnsavedChangesLogoutDialog'

interface HeaderProps {
  caseId?: string | null
}

export function Header({ caseId: _caseId }: HeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout, user } = useAuthStore()
  const isOnDashboard = location.pathname === '/dashboard'
  const isOnFormPage = location.pathname === '/form'
  const clearLocalDraft = useValuationStore((s) => s.clearLocalDraft)
  const isDirty = useValuationStore((s) => s.isDirty)
  const status = useValuationStore((s) => s.status)
  const deleteUnsavedServerPhotos = useValuationStore((s) => s.deleteUnsavedServerPhotos)
  const saveDraft = useSaveDraft()
  const initials = user?.email?.slice(0, 2).toUpperCase() ?? 'IN'
  const displayName = user?.email ?? 'Inspector'

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [dropdownOpen])

  const goToDashboard = useCallback(() => {
    // Clear form-recognizer photos when leaving the form page. If FormPage's unsaved-changes
    // blocker will intercept, it clears them itself on "Leave anyway" — clearing here would
    // wipe them even when the user chooses "Stay on Page".
    const { isDirty: dirty, status: formStatus } = useValuationStore.getState()
    const willBeBlocked = isOnFormPage && dirty && formStatus !== 'submitted'
    if (!isOnDashboard && !willBeBlocked) clearFormRecognizerPhotos()
    navigate('/dashboard')
  }, [isOnDashboard, isOnFormPage, navigate])

  const performLogout = useCallback(async () => {
    clearLocalDraft()
    await logout()
    navigate('/auth-login', { replace: true })
  }, [clearLocalDraft, logout, navigate])

  const handleLogout = () => {
    const hasUnsavedChanges = isOnFormPage && isDirty && status !== 'submitted'
    if (hasUnsavedChanges) {
      setDropdownOpen(false)
      setShowUnsavedDialog(true)
    } else {
      performLogout()
    }
  }

  const handleSaveDraftAndLogout = () => {
    saveDraft.mutate(undefined, {
      onSuccess: () => performLogout(),
    })
  }

  const handleLogoutWithoutSaving = useCallback(async () => {
    setIsDeleting(true)
    try {
      await deleteUnsavedServerPhotos()
    } finally {
      setIsDeleting(false)
      setShowUnsavedDialog(false)
      await performLogout()
    }
  }, [deleteUnsavedServerPhotos, performLogout])

  return (
    <>
    <UnsavedChangesLogoutDialog
      open={showUnsavedDialog}
      isSaving={saveDraft.isPending}
      isDeleting={isDeleting}
      onSaveDraftAndLogout={handleSaveDraftAndLogout}
      onLogoutWithoutSaving={handleLogoutWithoutSaving}
      onCancel={() => { if (!isDeleting) setShowUnsavedDialog(false) }}
    />
    <header
      className={cn(
        'flex items-center h-[60px] px-6 gap-4',
        'bg-white border-b border-[var(--border)]',
        'backdrop-blur-[20px] sticky top-0 z-[300]'
      )}
    >
      {/* Logo + title — clicking anywhere navigates to dashboard */}
      <div
        data-tour="dashboard-logo"
        className="flex items-center gap-[12px] flex-shrink-0 cursor-pointer"
        onClick={goToDashboard}
        title={getMessage('header.tooltip_go_to_dashboard')}
      >
        <img
          src={logo}
          alt="VVSI Logo"
          className="h-[44px] w-auto object-contain"
        />
        <div className="flex flex-col leading-tight">
          <span
            className="text-[15px] font-[800] tracking-[-0.3px] text-[var(--text)] hidden sm:block"
            style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}
          >
            {getMessage('header.app_name')} <span style={{ color: 'var(--accent)' }}>{getMessage('header.app_name_accent')}</span>
          </span>
        </div>
      </div>

      {/* Desktop: user chip + logout (lg and above) */}
      <div className="ml-auto hidden lg:flex items-center gap-[10px]">
        {/* Restart guided tour — desktop matches the neutral user-chip/logout button style
            next to it (thin border, surface2 fill, hover-accent), not the mobile thick circle.
            Only shown on the dashboard page — the tour is dashboard-specific. */}
        {isOnDashboard && (
          <button
            type="button"
            onClick={() => useTourUiStore.getState().requestTourRestart()}
            title={getMessage('tour.header.button_restart')}
            aria-label={getMessage('tour.header.button_restart')}
            className={cn(
              'flex items-center justify-center w-9 h-9 rounded-full',
              'border border-[var(--border)] bg-[var(--surface2)] text-[var(--text-dim)]',
              'hover:border-[var(--accent)] hover:text-[var(--accent)] hover:bg-[rgba(61,142,240,0.05)]',
              'transition-all duration-200'
            )}
          >
            <HelpCircle size={16} strokeWidth={2} />
          </button>
        )}

        {/* User chip */}
        <div
          className={cn(
            'flex items-center gap-2 px-3 py-[5px] pl-[6px]',
            'bg-[var(--surface2)] border border-[var(--border)] rounded-[20px]',
            'cursor-pointer text-[12px] text-[var(--text-dim)]',
            'hover:border-[var(--accent)] hover:text-[var(--accent)] transition-all duration-200',
          )}
          style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif', fontWeight: 600 }}
        >
          <div
            className={cn(
              'w-[26px] h-[26px] rounded-full flex items-center justify-center',
              'text-[10px] font-bold text-white'
            )}
            style={{ background: 'linear-gradient(135deg, var(--accent), var(--accent3))' }}
          >
            {initials}
          </div>
          {displayName}
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className={cn(
            'flex items-center gap-[5px] px-4 py-2 rounded-[var(--radius-sm)]',
            'border border-[var(--border-active)] bg-[rgba(61,142,240,0.05)]',
            'text-[var(--accent)] text-[11px] font-mono cursor-pointer',
            'hover:bg-[var(--accent)] hover:text-[var(--border)] hover:border-[var(--border-active)]',
            'transition-all duration-200'
          )}
        >
          <svg className="w-[13px] h-[13px] flex-shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 14H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h3" />
            <polyline points="10 11 14 8 10 5" />
            <line x1="14" y1="8" x2="6" y2="8" />
          </svg>
          {getMessage('header.button_logout')}
        </button>
      </div>

      {/* Mobile: profile (below lg) */}
      <div className="ml-auto lg:hidden flex items-center gap-2 flex-shrink-0">
      {/* Restart guided tour — matches the profile icon button's size/border/tint below.
          Only shown on the dashboard page — the tour is dashboard-specific. */}
      {isOnDashboard && (
        <button
          type="button"
          onClick={() => useTourUiStore.getState().requestTourRestart()}
          title={getMessage('tour.header.button_restart')}
          aria-label={getMessage('tour.header.button_restart')}
          className="w-[36px] h-[36px] rounded-full flex items-center justify-center flex-shrink-0 border-2 border-[#1e40af66] transition-all duration-200 cursor-pointer"
          style={{ backgroundColor: '#1e40af1f', color: '#1e40af' }}
        >
          <HelpCircle size={18} strokeWidth={2} />
        </button>
      )}

      <div className="relative flex-shrink-0" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setDropdownOpen((v) => !v)}
          aria-label={getMessage('header.aria_user_menu', { name: displayName })}
          className={cn(
            'w-[36px] h-[36px] rounded-full flex items-center justify-center',
            'border-2 transition-all duration-200 cursor-pointer',
            dropdownOpen
              ? 'border-[var(--accent)] bg-[rgba(61,142,240,0.1)] text-[var(--accent)]'
              : 'border-[#1e40af66]'
          )}
          style={!dropdownOpen ? { backgroundColor: '#1e40af1f', color: '#1e40af' } : undefined}
          title={displayName}
        >
          <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
          </svg>
        </button>

        {dropdownOpen && (
          <div
            className={cn(
              'absolute right-0 top-[calc(100%+8px)] z-[350]',
              'w-[260px] bg-[var(--surface)] border border-[var(--border)]',
              'rounded-[var(--radius-sm)] shadow-[0_8px_24px_rgba(0,0,0,0.10)]',
              'animate-fade-in overflow-hidden'
            )}
          >
            {/* Email row */}
            <div className="flex items-center gap-[10px] px-4 py-3 border-b border-[var(--border)] bg-[var(--surface2)]">
              <div
                className="w-[30px] h-[30px] rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-white"
                style={{ background: 'linear-gradient(135deg, var(--accent), var(--accent3))' }}
              >
                {initials}
              </div>
              <span className="text-[12.5px] text-[var(--text-dim)] break-all leading-[1.5]">
                {displayName}
              </span>
            </div>

            {/* Logout */}
            <div className="py-1">
              <button
                type="button"
                onClick={handleLogout}
                className={cn(
                  'w-full flex items-center justify-end gap-[10px] px-4 py-[9px]',
                  'text-[12.5px] text-[var(--accent)] cursor-pointer',
                  'hover:bg-[rgba(61,142,240,0.07)] transition-colors duration-150'
                )}
              >
                <svg className="w-[14px] h-[14px] flex-shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 14H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h3" />
                  <polyline points="10 11 14 8 10 5" />
                  <line x1="14" y1="8" x2="6" y2="8" />
                </svg>
                {getMessage('header.button_logout')}
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
    </header>
    </>
  )
}
